import { supabase } from '@/integrations/supabase/client';
import { ApiService } from '@/services/api/ApiService';
import { SavedQRCode, SaveQRCodeParams } from '@/types/qr-code/saved-qr-code';

const BUCKET_NAME = 'qr-codes';

export class QRCodeStorageService {
  /**
   * Save a generated QR code to storage and record metadata in the database.
   * Uploads file to storage on client, then saves metadata via API.
   */
  static async saveQRCode(params: SaveQRCodeParams): Promise<SavedQRCode> {
    const { agent_id, format, display_config, target_url, blob, filename } = params;

    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 10);
    const storagePath = `agent-${agent_id}/qr-${timestamp}-${random}.${format}`;

    // Upload to storage (client-side)
    const { error: uploadError } = await supabase.storage
      .from(BUCKET_NAME)
      .upload(storagePath, blob, {
        contentType: format === 'png' ? 'image/png' : 'image/svg+xml',
        cacheControl: '3600',
        upsert: false,
      });

    if (uploadError) {
      console.error('QR upload error:', uploadError);
      throw new Error(`Failed to upload QR code: ${uploadError.message}`);
    }

    // Get public URL
    const { data: { publicUrl } } = supabase.storage
      .from(BUCKET_NAME)
      .getPublicUrl(storagePath);

    try {
      // Save metadata via API
      const response = await ApiService.invoke<{ qrCode: SavedQRCode }>({
        resource: 'qr-code',
        action: 'create',
        data: {
          agent_id,
          storage_path: storagePath,
          storage_url: publicUrl,
          format,
          file_size: blob.size,
          display_config,
          target_url,
          filename,
        },
      });

      return response?.qrCode as SavedQRCode;
    } catch (error) {
      // Cleanup storage if API call fails
      await supabase.storage.from(BUCKET_NAME).remove([storagePath]);
      console.error('QR metadata save error:', error);
      throw error;
    }
  }

  /**
   * Fetch saved QR codes for an agent, ordered by most recent first.
   */
  static async getSavedQRCodes(agentId: string): Promise<SavedQRCode[]> {
    const response = await ApiService.invoke<{ items: SavedQRCode[] }>({
      resource: 'qr-code',
      action: 'all',
      data: {
        agentId,
      },
    });

    return response?.items || [];
  }

  /**
   * Soft delete a saved QR code.
   */
  static async deleteSavedQRCode(qrCodeId: string): Promise<void> {
    await ApiService.invoke({
      resource: 'qr-code',
      action: 'delete',
      id: qrCodeId,
    });
  }

  /**
   * Re-download a previously saved QR code and increment its download count.
   */
  static async redownloadQRCode(qrCode: SavedQRCode): Promise<void> {
    const response = await fetch(qrCode.storage_url);
    if (!response.ok) {
      throw new Error('Failed to fetch QR code file');
    }

    const blob = await response.blob();
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = qrCode.filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    // Increment download count (best-effort)
    try {
      await ApiService.invoke({
        resource: 'qr-code',
        action: 'increment-download',
        id: qrCode.id,
        data: {
          downloadCount: qrCode.download_count || 0,
        },
      });
    } catch (error) {
      console.error('Error incrementing download count:', error);
    }
  }
}
